// RetryDegradedSectionsFlow — the per-section remedy for a DEGRADED READY (ORP-20409, contract
// 07 §14). A 200 whose sections did not all resolve is a degraded ready: the boot records
// `{section, retryable}` on `sessionStore.degradedSections` and still reaches `ready`
// (`AccountBootstrapFlow.recordDegraded`). Until now the ONLY remedy was the whole-boot retry in
// `app/shell/useBootstrap`, which drops the session back through the splash and re-fetches every
// section — including the ones that already landed — to fix one that did not.
//
// Like the two switch flows this is HEADLESS: it never touches `session.status` (no splash — a
// section retry is not a re-boot, contract §7/§14), never logs anyone out, never resolves i18n, and
// RETURNS its outcome so the caller owns the copy. It writes through the same two doors the boot
// flow uses — 07's own stores for the sections 07 owns, `dispatchBootstrap` for everyone else's.
import {
  isBootstrapSection,
  type BootstrapSection,
  type MappedBootstrapResult,
} from '../models/bootstrap.types'
import type { WorkspaceItem } from '../models/workspace.types'
import { BootstrapService } from '../service/BootstrapService'
import {
  hydrateAccountDetail,
  hydrateFeatureFlags,
  hydrateRbac,
  hydrateSessionAccess,
} from '../service/hydrateOwn'
import { useAccountStore, type AccountStore } from '../stores/accountStore'
import { useSessionStore, type SessionStore } from '../stores/sessionStore'
import {
  useWorkspaceStore,
  type WorkspaceAttribute,
  type WorkspaceStore,
} from '../stores/workspaceStore'

import { toApiError } from './flowToolkit'
import { dispatchBootstrap } from './hydratorRegistry'

import type { ApiError } from '@/shared/http'

/**
 * Sections the backend refuses without a `workspace_id` (`WORKSPACE_REQUIRED_SECTIONS`) or that are
 * meaningless without one. Asking for any of them with no workspace selected 400s the WHOLE
 * request — which would turn a partial retry into a total failure — so they are filtered out and
 * left degraded instead (`skipped` in the outcome).
 */
const WORKSPACE_SCOPED_SECTIONS: ReadonlySet<BootstrapSection> = new Set<BootstrapSection>([
  'workspace_core',
  'dimensions',
  'compositions',
  'integrations',
  'settings',
  'feature_flags',
])

/** The outcome, returned rather than toasted (contract §13 — no i18n below the view). `retried`
 *  and `skipped` are reported separately so the caller can say "some sections still need a
 *  workspace" rather than claiming a clean success. */
export type RetryDegradedOutcome =
  | { status: 'nothingToRetry' }
  | { status: 'stale' }
  | { status: 'retried'; retried: BootstrapSection[]; skipped: BootstrapSection[] }
  | { status: 'failed'; error: ApiError }

export interface RetryDegradedSectionsFlowOptions {
  bootstrapService?: BootstrapService
  session?: SessionStore
  accountStore?: AccountStore
  workspaceStore?: WorkspaceStore
  dispatch?: typeof dispatchBootstrap
}

export class RetryDegradedSectionsFlow {
  private readonly bootstrapService: BootstrapService
  private readonly session: SessionStore
  private readonly accountStore: AccountStore
  private readonly workspaceStore: WorkspaceStore
  private readonly dispatch: typeof dispatchBootstrap

  constructor(options: RetryDegradedSectionsFlowOptions = {}) {
    this.bootstrapService = options.bootstrapService ?? new BootstrapService()
    this.session = options.session ?? useSessionStore()
    this.accountStore = options.accountStore ?? useAccountStore()
    this.workspaceStore = options.workspaceStore ?? useWorkspaceStore()
    this.dispatch = options.dispatch ?? dispatchBootstrap
  }

  /**
   * Re-ask for the retryable degraded sections only.
   *   • nothing retryable (or no account) → `nothingToRetry`, ZERO network. A section the server
   *     marked `retryable: false` is a deterministic 4xx; re-asking would fail identically.
   *   • the account/workspace moved while the request was in flight → `stale`, writing NOTHING.
   *   • otherwise → one `bootSections` fetch with `refresh: true`, hydrate, settle the record.
   *
   * `refresh: true` is not optional: the degraded section is exactly the one whose cached value is
   * missing or stale, so a cache hit is the one answer that cannot help (same reasoning as
   * `LoadAccountContextFlow`'s post-Stripe force).
   */
  public async execute(): Promise<RetryDegradedOutcome> {
    const accountId = this.accountStore.accountId
    if (accountId === undefined) return { status: 'nothingToRetry' }

    const retryable = this.session.degradedSections
      .filter((entry) => entry.retryable)
      .map((entry) => entry.section)
      // A section name this client cannot model must never reach the request — it would 400 the
      // whole call and turn a partial retry into a total failure.
      .filter(isBootstrapSection)
    if (!retryable.length) return { status: 'nothingToRetry' }

    const workspaceId = this.workspaceStore.workspaceId
    const hasWorkspace = workspaceId !== undefined
    const attempted = retryable.filter(
      (section) => hasWorkspace || !WORKSPACE_SCOPED_SECTIONS.has(section),
    )
    const skipped = retryable.filter((section) => !attempted.includes(section))
    if (!attempted.length) return { status: 'nothingToRetry' }

    let result: MappedBootstrapResult
    try {
      result = await this.bootstrapService.bootSections(
        Number(accountId),
        [...attempted],
        workspaceId,
        { refresh: true },
      )
    } catch (error) {
      return { status: 'failed', error: toApiError(error) }
    }

    // Staleness guard, same shape as the boot flow's deferred-compositions one: a late response for
    // a context the user has already left must write NOTHING, or a switch silently inherits the
    // previous workspace's data.
    if (!this.isCurrentContext(accountId, workspaceId)) return { status: 'stale' }

    this.hydrate(result, attempted)
    this.session.resolveDegradedSections(attempted, result.degradedSections ?? [])
    return { status: 'retried', retried: [...attempted], skipped }
  }

  /** 07's own stores for the sections 07 owns, the registered hydrators for the rest — the same
   *  split `flowToolkit` makes, narrowed to the sections actually re-asked for. A broader hydrate
   *  would write fields this response never carried. */
  private hydrate(result: MappedBootstrapResult, attempted: readonly BootstrapSection[]): void {
    const asked = new Set<BootstrapSection>(attempted)
    if (asked.has('session_access')) hydrateSessionAccess(this.accountStore, result)
    if (asked.has('account_detail')) hydrateAccountDetail(this.accountStore, result)
    if (asked.has('rbac')) hydrateRbac(this.accountStore, result)
    if (asked.has('account_core') && result.workspaces) {
      this.workspaceStore.setWorkspaces(result.workspaces as unknown as WorkspaceItem[])
    }
    if (asked.has('settings') && result.attributes) {
      this.workspaceStore.setAttributes(result.attributes as WorkspaceAttribute[])
    }
    if (asked.has('feature_flags')) hydrateFeatureFlags(this.workspaceStore, result)
    this.dispatch(result, attempted)
  }

  private isCurrentContext(accountId: number | string, workspaceId: number | string | undefined) {
    return (
      String(this.accountStore.accountId ?? '') === String(accountId) &&
      String(this.workspaceStore.workspaceId ?? '') === String(workspaceId ?? '')
    )
  }
}
